import React, { useState, useEffect } from "react";
import { 
  Plus, 
  Ticket, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Loader2,
  MessageSquare,
  ArrowUpCircle,
  ChevronDown,
  RefreshCw
} from "lucide-react";
import Layout from "../components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "../components/ToastProvider";
import { apiRequest, formatDate, cn } from "../lib/utils";

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNovoTicket, setShowNovoTicket] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedTicket, setExpandedTicket] = useState(null);
  
  // Form state
  const [novoTicket, setNovoTicket] = useState({
    tipo: "problema",
    titulo: "",
    descricao: ""
  });

  const fetchTickets = async () => {
    try {
      const data = await apiRequest("/tickets");
      setTickets(data);
    } catch (error) {
      toast.error("Erro ao carregar tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleSubmit = async () => {
    if (!novoTicket.titulo.trim()) {
      toast.error("Informe o título do ticket");
      return;
    }
    if (!novoTicket.descricao.trim()) {
      toast.error("Descreva o problema ou sugestão");
      return;
    }

    setSaving(true);
    try {
      await apiRequest("/tickets", {
        method: "POST",
        body: JSON.stringify(novoTicket)
      });
      toast.success("Ticket aberto com sucesso! Entraremos em contato em breve.");
      setShowNovoTicket(false);
      setNovoTicket({ tipo: "problema", titulo: "", descricao: "" });
      fetchTickets();
    } catch (error) {
      toast.error(error.message || "Erro ao criar ticket");
    } finally {
      setSaving(false);
    }
  };

  // Marcar ticket como lido quando expandir
  const handleExpandTicket = async (ticketId) => {
    const ticket = tickets.find(t => t.id === ticketId);
    
    if (expandedTicket === ticketId) {
      setExpandedTicket(null);
    } else {
      setExpandedTicket(ticketId);
      
      // Se o ticket não foi lido, marcar como lido
      if (ticket && ticket.lido_pelo_usuario === false) {
        try {
          await apiRequest(`/tickets/${ticketId}/marcar-lido`, { method: "PUT" });
          // Atualizar local
          setTickets(prev => prev.map(t => 
            t.id === ticketId ? { ...t, lido_pelo_usuario: true } : t
          ));
        } catch (error) {
          console.error("Erro ao marcar ticket como lido:", error);
        }
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "aberto":
        return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" />Aberto</Badge>;
      case "em_andamento":
        return <Badge className="bg-blue-100 text-blue-800"><Loader2 className="w-3 h-3 mr-1" />Em Andamento</Badge>;
      case "resolvido":
        return <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="w-3 h-3 mr-1" />Resolvido</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTipoBadge = (tipo) => {
    switch (tipo) {
      case "problema":
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Problema</Badge>;
      case "melhoria":
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Melhoria</Badge>;
      case "duvida":
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Dúvida</Badge>;
      case "outros":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Outros</Badge>;
      default:
        return <Badge variant="outline">{tipo}</Badge>;
    }
  };

  // Contadores
  const ticketsAbertos = tickets.filter(t => t.status === "aberto").length;
  const ticketsEmAndamento = tickets.filter(t => t.status === "em_andamento").length;
  const ticketsResolvidos = tickets.filter(t => t.status === "resolvido").length;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Suporte
            </h1>
            <p className="text-slate-600">
              Abra tickets para relatar problemas ou sugerir melhorias
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchTickets}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            <Button onClick={() => setShowNovoTicket(true)} className="bg-teal-600 hover:bg-teal-700">
              <Plus className="w-4 h-4 mr-2" />
              Novo Ticket
            </Button>
          </div>
        </div>

        {/* Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Ticket className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{tickets.length}</p>
                  <p className="text-sm text-slate-500">Total de Tickets</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{ticketsAbertos}</p>
                  <p className="text-sm text-slate-500">Abertos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Loader2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{ticketsEmAndamento}</p>
                  <p className="text-sm text-slate-500">Em Andamento</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{ticketsResolvidos}</p>
                  <p className="text-sm text-slate-500">Resolvidos</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lista de Tickets */}
        <Card>
          <CardHeader>
            <CardTitle>Meus Tickets</CardTitle>
            <CardDescription>Histórico de solicitações de suporte</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-12">
                <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-700">Nenhum ticket aberto</h3>
                <p className="text-slate-500 mb-4">Você ainda não abriu nenhum ticket de suporte</p>
                <Button onClick={() => setShowNovoTicket(true)} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Abrir Primeiro Ticket
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <div 
                    key={ticket.id} 
                    className={cn(
                      "border rounded-lg overflow-hidden",
                      ticket.lido_pelo_usuario === false && "border-amber-300 bg-amber-50/30"
                    )}
                  >
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                      onClick={() => handleExpandTicket(ticket.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-slate-400">{ticket.numero}</span>
                            {getTipoBadge(ticket.tipo)}
                            {ticket.lido_pelo_usuario === false && (
                              <Badge className="bg-amber-500 text-white text-xs">Nova resposta</Badge>
                            )}
                          </div>
                          <h3 className="font-medium text-slate-900">{ticket.titulo}</h3>
                          <p className="text-sm text-slate-500">
                            Aberto em {formatDate(ticket.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(ticket.status)}
                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedTicket === ticket.id ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                    
                    {expandedTicket === ticket.id && (
                      <div className="border-t bg-slate-50 p-4 space-y-4">
                        <div>
                          <h4 className="text-sm font-medium text-slate-700 mb-1">Descrição:</h4>
                          <p className="text-sm text-slate-600 whitespace-pre-wrap">{ticket.descricao}</p>
                        </div>
                        
                        {ticket.resposta && (
                          <div className="bg-white border rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <MessageSquare className="w-4 h-4 text-teal-600" />
                              <h4 className="text-sm font-medium text-teal-700">Resposta do Suporte:</h4>
                            </div>
                            <p className="text-sm text-slate-600 whitespace-pre-wrap">{ticket.resposta}</p>
                          </div>
                        )}
                        
                        {ticket.resolved_at && (
                          <p className="text-xs text-slate-400">
                            Resolvido em {formatDate(ticket.resolved_at)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal Novo Ticket */}
        <Dialog open={showNovoTicket} onOpenChange={setShowNovoTicket}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-teal-600" />
                Abrir Novo Ticket
              </DialogTitle>
              <DialogDescription>
                Descreva seu problema ou sugestão de melhoria
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select 
                  value={novoTicket.tipo} 
                  onValueChange={(v) => setNovoTicket({...novoTicket, tipo: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="problema">🔴 Problema</SelectItem>
                    <SelectItem value="melhoria">🟣 Sugestão de Melhoria</SelectItem>
                    <SelectItem value="duvida">🔵 Dúvida</SelectItem>
                    <SelectItem value="outros">🟢 Outros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input 
                  placeholder="Resumo do problema ou sugestão"
                  value={novoTicket.titulo}
                  onChange={(e) => setNovoTicket({...novoTicket, titulo: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Descrição *</Label>
                <Textarea 
                  placeholder="Descreva detalhadamente o problema ou sua sugestão de melhoria..."
                  value={novoTicket.descricao}
                  onChange={(e) => setNovoTicket({...novoTicket, descricao: e.target.value})}
                  rows={5}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNovoTicket(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={saving}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUpCircle className="w-4 h-4 mr-2" />}
                Enviar Ticket
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Tickets;
